import React, { ReactNode, useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Navbar from './Navbar';
import Footer from './Footer';
import { useScrollToHash } from '../../hooks/useScrollToHash';
import type { LayoutProps } from '../../types';

// Error fallback component
const ErrorFallback: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Something went wrong</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-4">Please try refreshing the page</p>
      <button 
        onClick={() => window.location.reload()}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors duration-200"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

// Loading fallback component
export const LoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500">
      <span className="sr-only">Loading...</span>
    </div>
  </div>
);

const MainLayout: React.FC<LayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Use the scroll to hash hook
  useScrollToHash();
  
  // Toggle mobile menu with memoized callback
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <Navbar 
        toggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
      />
      
      <main className="flex-grow pt-16">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {children}
        </ErrorBoundary>
      </main>
      
      <Footer />
      {/* Removed ScrollRestoration component */}
    </div>
  );
};

export default MainLayout;